import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { judgmentSubmitSchema, toToneMarks } from "@xinghai/shared";
import type { JudgmentSubmitPayload } from "@xinghai/shared";
import { Button, EmptyState, Icon, Skeleton, Tag, useConfirmSafe, useToast } from "@xinghai/ui";
import { ApiError, annotateApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { stripTone as dropToneMarks } from "../student/taskMeta";
import { AudioPanel } from "./components/AudioPanel";
import { CharRows } from "./components/CharRows";
import { SingleCharWorkbench } from "./components/SingleCharWorkbench";
import { UtterancePanel } from "./components/UtterancePanel";
import { RubricGuide } from "./components/RubricGuide";
import { AnnotatorGuide } from "./components/AnnotatorGuide";
import {
  RETRACT_WINDOW_MS,
  applyMarkDefaults,
  armRetract,
  buildPayload,
  clearDraft,
  clearRetract,
  emptyChar,
  emptyDraft,
  loadDraft,
  readPace,
  readQuickMode,
  readRetract,
  recordSubmission,
  recordTiming,
  saveDraft,
} from "./draft";
import type { CharDraft, RetractTicket, WorkDraft } from "./draft";
import {
  DOMAIN_LABEL,
  TASK_LABEL_EXPERT,
  dimensionLabel,
  formatDuration,
  formatRemaining,
} from "./labels";
import { workHref } from "./QueuePage";
import { useMediaQuery } from "./useMediaQuery";
import type { AnnotateDetail } from "./types";

const AUTOSAVE_MS = 700;

/** zod issues → one readable Chinese line per problem. */
function issueLines(payload: JudgmentSubmitPayload): string[] {
  const parsed = judgmentSubmitSchema.safeParse(payload);
  if (parsed.success) return [];
  return parsed.error.issues.map((i) => {
    const [head, idx, dim] = i.path as (string | number)[];
    if (head === "chars" && typeof idx === "number") {
      return `第 ${idx + 1} 字 · ${dimensionLabel(String(dim ?? ""))}：${i.message}`;
    }
    if (head === "utterance") return `句级 · ${dimensionLabel(String(idx ?? ""))}：${i.message}`;
    return `${dimensionLabel(String(head ?? ""))}：${i.message}`;
  });
}

/** docs/UX.md 7 — 标注工作台. */
export function WorkbenchPage() {
  const { recordingId = "" } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirmSafe();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const userId = me?.user.id ?? "anon";
  // Three columns only at ≥1100px; below that the audio panel sits on top in
  // its compact form (docs/UX.md 7).
  const stacked = useMediaQuery("(max-width: 1099px)");

  const q = useQuery({
    queryKey: qk.annotateItem(recordingId),
    queryFn: () => annotateApi.get(recordingId),
    enabled: recordingId.length > 0,
  });
  const detail: AnnotateDetail | undefined = q.data;

  /** docs/ROADMAP.md A3 — 单字任务 gets no 句级 rubric. */
  const singleChar =
    !!detail && detail.material.chars.length === 1 && detail.recording.task === "T2_diagnostic";
  /** docs/ROADMAP.md B3 — 快速模式 only ever applies to those 单字 items. */
  const quickMode = useMemo(
    () => singleChar && (search.get("quick") === "1" || readQuickMode()),
    [singleChar, search],
  );

  // Only the 单字 screen shows a 「37/240 · 剩约 …」 line, so only it needs the
  // queue counts.
  const queueQ = useQuery({
    queryKey: qk.annotateQueue({}),
    queryFn: () => annotateApi.queue({}),
    enabled: singleChar,
    retry: false,
  });

  const [draft, setDraft] = useState<WorkDraft | null>(null);
  const [restored, setRestored] = useState(false);
  const [active, setActive] = useState(0);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [retract, setRetract] = useState<RetractTicket | null>(null);
  /** Recording we just 撤回'd — its cached `submitted` flag must not lock us out. */
  const [retractedId, setRetractedId] = useState<string | null>(null);
  const startedRef = useRef<number>(Date.now());

  // ---- draft bootstrap (server judgment > localStorage draft > blank) ----
  useEffect(() => {
    if (!detail) return;
    const domain = detail.domain;
    const count = detail.material.chars.length;
    const fresh = emptyDraft(recordingId, domain, count);
    // 辙位 defaults to the material's 辙 so the expert only changes exceptions.
    if (domain === "singing") {
      fresh.chars = fresh.chars.map((c, i) => ({
        ...c,
        zhe_realized: detail.material.chars[i]?.zhe ?? null,
      }));
    }
    // 快速模式 pre-marks every character 正确; the expert only flags exceptions.
    if (quickMode) {
      fresh.chars = fresh.chars.map((c) => applyMarkDefaults(c, domain, "correct"));
    }
    const saved = loadDraft(recordingId, userId);
    if (saved && saved.domain === domain && saved.chars.length === count) {
      setDraft(saved);
      setRestored(saved.chars.some((c) => c.touched));
      startedRef.current = saved.started_at || Date.now();
    } else {
      setDraft(fresh);
      setRestored(false);
      startedRef.current = Date.now();
    }
    setActive(0);
    setExpandedRow(null);
    setIssues([]);
  }, [detail, recordingId, userId, quickMode]);

  // ---- 5 s 撤回 window handed over from the item we just submitted ----
  useEffect(() => {
    setRetract(readRetract());
  }, [recordingId]);

  // ---- debounced autosave ----
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => saveDraft(userId, draft), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [draft, userId]);

  // ---- elapsed timer ----
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const setChar = useCallback((index: number, next: CharDraft) => {
    setDraft((d) => {
      if (!d) return d;
      const chars = d.chars.slice();
      chars[index] = next;
      return { ...d, chars };
    });
  }, []);

  const submit = useMutation({
    mutationFn: (payload: JudgmentSubmitPayload) => annotateApi.submit(recordingId, payload),
    onSuccess: async () => {
      clearDraft(recordingId, userId);
      recordSubmission(userId);
      recordTiming(userId, Date.now() - startedRef.current);
      armRetract(recordingId);
      setRetractedId(null);
      void queryClient.invalidateQueries({ queryKey: qk.annotateQueue({}) });
      void queryClient.invalidateQueries({ queryKey: qk.qualityAnnotators });
      void queryClient.removeQueries({ queryKey: qk.annotateItem(recordingId) });
      let nextId: string | null = null;
      try {
        const queue = await queryClient.fetchQuery({
          queryKey: qk.annotateQueue({}),
          queryFn: () => annotateApi.queue({}),
        });
        nextId = queue.items.find((i) => i.recording_id !== recordingId)?.recording_id ?? null;
      } catch {
        nextId = null;
      }
      const suffix = quickMode ? "?quick=1" : "";
      void navigate(nextId ? `${workHref(nextId)}${suffix}` : "/a");
    },
    onError: (err) => {
      if (err instanceof ApiError && Array.isArray(err.details)) {
        setIssues(
          (err.details as Array<{ path?: unknown[]; message?: string }>).map(
            (d) => `${(d.path ?? []).join(".")}：${d.message ?? "校验未通过"}`,
          ),
        );
      }
      toast.error(err instanceof ApiError ? err.message : "提交失败，请重试。");
    },
  });

  const undo = useMutation({
    mutationFn: (id: string) => annotateApi.retract(id),
    onSuccess: async (_r, id) => {
      clearRetract();
      setRetract(null);
      // The item query is still cached (and inside its 15 s staleTime) with
      // `submitted: true`, so drop it and force a refetch — otherwise the
      // workbench reopens read-only right after a successful 撤回.
      setRetractedId(id);
      queryClient.removeQueries({ queryKey: qk.annotateItem(id) });
      await queryClient.invalidateQueries({
        queryKey: qk.annotateItem(id),
        refetchType: "all",
      });
      void queryClient.invalidateQueries({ queryKey: qk.annotateQueue({}) });
      void queryClient.invalidateQueries({ queryKey: qk.annotateRereview });
      void queryClient.invalidateQueries({ queryKey: qk.qualityAnnotators });
      toast.info("已撤回，重新打开那一条。");
      void navigate(workHref(id, quickMode));
    },
    onError: (err) => {
      clearRetract();
      setRetract(null);
      toast.error(
        err instanceof ApiError && err.status === 409
          ? "已经不能撤回了：这条判断已经进入合并。"
          : "撤回失败，请稍后再试。",
      );
    },
  });

  const elapsedMs = draft ? now - startedRef.current : 0;
  const retractLeft = retract
    ? Math.max(0, Math.ceil((RETRACT_WINDOW_MS - (now - retract.at)) / 1000))
    : 0;
  useEffect(() => {
    if (retract && retractLeft === 0) setRetract(null);
  }, [retract, retractLeft]);

  const handleSubmit = useCallback(
    async (skipConfirm = false, charOverride?: CharDraft) => {
      if (!draft || !detail) return;
      // 「正确」 is one click: use the value we just computed instead of waiting
      // for the state update to land.
      const effective = charOverride ? { ...draft, chars: [charOverride] } : draft;
      const payload = buildPayload(effective, Date.now() - startedRef.current, {
        skipUtterance: singleChar,
      });
      const lines = issueLines(payload);
      setIssues(lines);
      if (lines.length > 0) {
        toast.error("还有几项没通过校验，请看右栏的提示。");
        return;
      }
      // docs/UX.md §2.2 — 单字 never shows a confirm dialog; 撤回 covers it.
      if (!skipConfirm && !singleChar) {
        const ok = await confirm({
          title: "提交这条判断？",
          message: "提交后不可撤回，系统会立即把它和另一位专家的判断合并。",
          confirmLabel: "确认提交",
          cancelLabel: "再看看",
        });
        if (!ok) return;
      }
      submit.mutate(payload);
    },
    [draft, detail, singleChar, confirm, submit, toast],
  );

  // 快速模式: Enter submits straight away and the queue advances (B3).
  useEffect(() => {
    // The 单字 screen owns its own key map (Enter/X/1–6) — don't double-bind.
    if (!quickMode || singleChar || typeof document === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable)
        return;
      e.preventDefault();
      void handleSubmit(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [quickMode, singleChar, handleSubmit]);

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={140} />
        <Skeleton variant="rect" height={320} />
      </div>
    );
  }

  if (q.isError || !detail || !draft) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-search" size={24} />}
          title="打不开这条录音"
          description="它可能已经不在你的队列里了。"
          action={
            <Link className="gz-button gz-button--primary" to="/a">
              返回队列
            </Link>
          }
        />
      </div>
    );
  }

  const chars = detail.material.chars;
  const singing = detail.domain === "singing";
  const done = draft.chars.filter((c) => c.touched).length;
  const locked = detail.submitted && retractedId !== detail.recording.id;
  const firstChar = chars[0];

  /** 红线 14.1 — the singing screen shows no tone mark at all. */
  const pinyinFor = (locked_: string) =>
    singing ? dropToneMarks(toToneMarks(locked_)) : toToneMarks(locked_);

  const pace = readPace(userId);
  const queueLeft = queueQ.data?.counts.queued ?? queueQ.data?.items.length ?? 0;
  const sessionDone = pace.count;
  const sessionTotal = pace.count + queueLeft;
  const remainingMs = pace.avgMs === null ? null : pace.avgMs * queueLeft;

  return (
    <div className="xz-workbench">
      <header className="xz-workbench__bar">
        <Link className="gz-button gz-button--quiet" to="/a">
          <Icon id="ui-arrow-left" size={20} /> 队列
        </Link>
        <span className="gz-type-data">{detail.recording.id}</span>
        <Tag tone="info">
          {TASK_LABEL_EXPERT[detail.recording.task]} · {DOMAIN_LABEL[detail.domain]}
        </Tag>
        <span className="gz-caption">{formatDuration(detail.recording.duration_ms)}</span>
        <span className="gz-caption xz-workbench__progress" role="status">
          {singleChar ? (
            <>
              {sessionDone}/{sessionTotal} · 剩约 {formatRemaining(remainingMs)}
            </>
          ) : (
            <>
              {done}/{chars.length} 字
            </>
          )}
        </span>
      </header>

      {/* The window also shows when you land on the submitted item itself. */}
      {retract && (
        <p className="gz-notice xz-workbench__retract" role="status">
          <Icon id="ui-undo" size={20} />
          上一条已提交。
          <Button
            variant="ghost"
            loading={undo.isPending}
            onClick={() => undo.mutate(retract.recording_id)}
          >
            撤回（{retractLeft} 秒）
          </Button>
        </p>
      )}

      {restored && (
        <p className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          已恢复上次的草稿（未提交）。
          <Button
            variant="ghost"
            onClick={() => {
              clearDraft(recordingId, userId);
              const fresh = emptyDraft(recordingId, detail.domain, chars.length);
              if (singing) {
                fresh.chars = fresh.chars.map((_, i) => ({
                  ...emptyChar(detail.domain),
                  zhe_realized: chars[i]?.zhe ?? null,
                }));
              }
              startedRef.current = Date.now();
              setDraft(fresh);
              setRestored(false);
            }}
          >
            清空重新标
          </Button>
        </p>
      )}

      {quickMode && !locked && (
        <p className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          快速模式：音频自动播放，默认已标「正确」，只标异常；按 Enter 直接提交并进入下一条。
        </p>
      )}

      {locked && (
        <p className="gz-notice" role="status">
          <Icon id="ui-lock" size={20} /> 这条你已经提交过，下面只读。
        </p>
      )}

      <RubricGuide domain={detail.domain} />
      <AnnotatorGuide domain={detail.domain} />

      {singleChar && firstChar ? (
        <SingleCharWorkbench
          audioUrl={detail.audio_url}
          char={firstChar}
          pinyinDisplay={pinyinFor(firstChar.pinyin_locked)}
          domain={detail.domain}
          value={draft.chars[0] ?? emptyChar(detail.domain)}
          onChange={(next) => setChar(0, next)}
          draft={draft}
          onDraftChange={setDraft}
          expanded={expandedRow === 0}
          onExpandedChange={(open) => setExpandedRow(open ? 0 : null)}
          submitting={submit.isPending}
          onSubmit={(nextChar) => void handleSubmit(true, nextChar)}
          disabled={locked}
          issues={issues}
        />
      ) : (
        <div className="xz-workbench__grid">
          <aside className="xz-workbench__audio">
            <AudioPanel
              src={detail.audio_url}
              compact={stacked}
              autoPlay={quickMode}
              charMarkers={chars.map((c) => c.char)}
              activeMarker={active}
              onPickMarker={setActive}
              label={`录音 ${detail.recording.id}`}
            />
            {detail.material.prompt && <p className="gz-caption">{detail.material.prompt}</p>}
          </aside>

          <main className="xz-workbench__chars">
            <CharRows
              chars={chars}
              pinyin={chars.map((c) => pinyinFor(c.pinyin_locked))}
              domain={detail.domain}
              values={draft.chars}
              onChange={setChar}
              expanded={expandedRow}
              onExpandedChange={setExpandedRow}
              active={active}
              onActivate={setActive}
              disabled={locked}
            />
          </main>

          <aside className="xz-workbench__utterance">
            <UtterancePanel
              domain={detail.domain}
              draft={draft}
              onChange={setDraft}
              elapsedMs={elapsedMs}
              progress={{ done, total: chars.length }}
              submitting={submit.isPending}
              onSubmit={() => void handleSubmit()}
              issues={issues}
              disabled={locked}
              inlineRubric
            />
          </aside>
        </div>
      )}
    </div>
  );
}
